import { useLayoutEffect, useRef, type RefObject } from 'react'
import { create } from 'zustand'

/**
 * Shared plumbing for the kit's modal surfaces (Modal, alerts, action sheets).
 * Each is a native <dialog> opened with showModal(), which puts it in the top
 * layer, makes the rest of the page inert, keeps Tab inside it and gives us a
 * ::backdrop. Escape arrives as a `cancel` event and is routed to `onCancel`
 * so the owner keeps deciding whether the overlay may be dismissed.
 */

/** Open modal dialogs, bottom → top. Toasts portal into the top one so they stay visible and interactive. */
export const useDialogStack = create<{ stack: HTMLDialogElement[] }>(() => ({ stack: [] }))

/* Scroll lock: ref-counted so stacked dialogs release it only once the last closes.
   Applied to <html> (document scrolling) and <body>; the gutter is reserved so
   the page doesn't shift when a classic scrollbar disappears. */
let scrollLocks = 0

function lockScroll () {
  scrollLocks += 1
  if (scrollLocks > 1) return
  const root = document.documentElement
  const hadScrollbar = window.innerWidth - root.clientWidth > 0
  root.classList.add('ui-scroll-locked')
  if (hadScrollbar) root.classList.add('ui-scroll-locked--gutter')
}

function unlockScroll () {
  scrollLocks = Math.max(0, scrollLocks - 1)
  if (scrollLocks > 0) return
  document.documentElement.classList.remove('ui-scroll-locked', 'ui-scroll-locked--gutter')
}

export interface ModalDialogOptions {
  /** Escape / browser-initiated close. Dismiss here, or ignore to stay open. */
  onCancel: () => void
  /** Element to focus once open; defaults to the dialog's `[autofocus]` element. */
  initialFocus?: () => HTMLElement | null | undefined
}

/**
 * Opens `ref`'s <dialog> modally for as long as the calling component is
 * mounted, and on unmount closes it and returns focus to whatever had it before.
 */
export function useModalDialog (ref: RefObject<HTMLDialogElement | null>, options: ModalDialogOptions) {
  const latest = useRef(options)
  latest.current = options

  useLayoutEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null
    let unmounting = false

    const onCancelEvent = (e: Event) => {
      e.preventDefault()
      latest.current.onCancel()
    }
    // Browsers only let `cancel` be prevented once per user activation, so a
    // second Escape can close the dialog outright. Re-open and let the owner decide.
    // `close` is dispatched as a later task, so a close() from a previous mount's
    // cleanup (StrictMode re-runs effects) lands here after we've re-opened it —
    // ignore it while the dialog is open again.
    const onClose = () => {
      if (unmounting || !dialog.isConnected || dialog.open) return
      dialog.showModal()
      latest.current.onCancel()
    }

    dialog.addEventListener('cancel', onCancelEvent)
    dialog.addEventListener('close', onClose)
    if (!dialog.open) dialog.showModal()
    const target = latest.current.initialFocus?.() ?? dialog.querySelector<HTMLElement>('[autofocus]')
    target?.focus({ preventScroll: true })
    useDialogStack.setState((s) => ({ stack: [...s.stack, dialog] }))
    lockScroll()

    return () => {
      unmounting = true
      dialog.removeEventListener('cancel', onCancelEvent)
      dialog.removeEventListener('close', onClose)
      useDialogStack.setState((s) => ({ stack: s.stack.filter((d) => d !== dialog) }))
      unlockScroll()
      if (dialog.open) dialog.close()
      if (previouslyFocused?.isConnected) previouslyFocused.focus({ preventScroll: true })
    }
  }, [ref])
}
